//----------------------------------------
// scanbot SDK ファサード
//----------------------------------------
// scandit/sdk.jsx と同様に、SDK初期化とカメラをアプリ内で1つだけ保持するモジュール。
// カメラ描画先は body 直下の常駐コンテナに置き、スキャン画面表示中だけ
// 画面内のスロットへ付け替える。これによりスキャン画面が未表示でもカメラを起動できる。
// カメラの開閉タイミングは scandit（enableCamera）に合わせている。

import { createHost, attachHost, detachHost, playVideos, setHostTransparent, refreshHostLayout, logCameraCapabilities, applyCameraFocus } from "./scanHost";
import { getCameraResolution, getCameraResolutionSize } from "./cameraResolutionSetting";
import { getCameraFocus } from "./cameraFocusSetting";

let scanbot = null;
let initPromise = null;
//SDKクラス（カメラ一覧は静的プロパティ ScanbotSDK.cameras から取得する
let ScanbotSDKClass = null;
//背面カメラのdeviceId（一度取得したら使い回す
let backCameraId = null;
//常駐スキャナー（アプリ内で1つだけ生成
let scanner = null;
//カメラ起動／停止の直列化
let opChain = Promise.resolve();

function enqueue(fn) {
	const next = opChain.then(fn, fn);
	opChain = next.catch(() => {});
	return next;
}
//スキャン結果の受け取り先（表示中のスキャン画面
let scanHandler = null;
let errorHandler = null;
//"barcode" / "qr"
let scanMode = null;
//デコード中かどうか
let decodingActive = false;
//バックグラウンド復帰の監視を登録済みか
let visibilityHandlersRegistered = false;
//復帰処理の多重実行防止（visibilitychangeとfocusが同時に発火するため
let resuming = false;

//WASM等のエンジン配置先（public/scanbot-sdk/
const ENGINE_PATH = "/scanbot-sdk/";

//カメラ描画先のID
export const SCANBOT_CONTAINER_ID = "scanbot-container";

//createBarcodeScanner の待ち上限
//※iOS復帰直後などカメラが解放しきれていないと完了しないことがあるため
const CREATE_SCANNER_TIMEOUT = 10000;

//----------------------------------------
//タイムアウト付きawait
//----------------------------------------
// 完了しない場合に処理全体が固まらないようにする。
// 後から解決した場合は使われないスキャナーを破棄する。

function withTimeout(promise, ms, label) {
	let timer = null;

	const timeout = new Promise((resolve, reject) => {
		timer = setTimeout(() => {
			//遅れて生成された場合は破棄しておく
			promise.then((result) => {
				try {
					result?.dispose?.();
				} catch (e) {}
			}).catch(() => {});

			reject(new Error(label + " timeout (" + ms + "ms)"));
		}, ms);
	});

	return Promise.race([promise, timeout]).finally(() => {
		if (timer) clearTimeout(timer);
	});
}

//----------------------------------------
//ライセンスキー取得
//----------------------------------------

export function getScanbotLicenseKey() {
	//未設定の場合は空文字（トライアルモード：1セッション60秒）
	return process.env.REACT_APP_SCANBOT_LICENSE || "";
}

//----------------------------------------
//初期化（多重実行防止
//----------------------------------------

export async function initScanbot() {
	if (scanbot) {
		return scanbot;
	}
	if (initPromise) {
		return initPromise;
	}

	console.log("【scanbot】initialize");

	initPromise = (async () => {
		try {
			//SDK本体は選択時のみ読み込む（初期表示のバンドルサイズ対策
			const ScanbotSDK = (await import("scanbot-web-sdk")).default;
			ScanbotSDKClass = ScanbotSDK;

			scanbot = await ScanbotSDK.initialize({
				licenseKey: getScanbotLicenseKey(),
				enginePath: ENGINE_PATH,
				//複数カメラ端末での「メインカメラ」判定方法（ラベル解析：500ms以内
				bestCameraDetectionStrategy: "FAST",
			});
			console.log("【scanbot】initialize done");

			//ライセンスの状態をログへ（barkoderと違いサーバー確認は不要な OFFLINE ライセンスだが、
			//有効期限があるため実機で確認できるようにする。※初期化は待たせない
			logLicenseInfo(scanbot);

			return scanbot;
		} finally {
			initPromise = null;
		}
	})();

	return initPromise;
}

//----------------------------------------
//ライセンス状態のログ出力
//----------------------------------------
// scanbot のライセンスは OFFLINE 形式で、サーバー確認を伴わずローカルで検証される。
// そのため barkoder のような「サーバー応答が間に合わずライセンス未適用になる」問題は起きない。
// ただし有効期限があるため、切れていないかを実機で確認できるようにしておく。

async function logLicenseInfo(instance) {
	try {
		if (typeof instance?.getLicenseInfo !== "function") return;

		const info = await instance.getLicenseInfo();
		console.log("-------------------------");
		console.log("【scanbot】license");
		console.log("isValid: " + info?.isValid);
		console.log("status: " + info?.status);
		console.log("message: " + info?.licenseStatusMessage);
		console.log("expiration: " + info?.expirationDateString);
		console.log("-------------------------");
	} catch (e) {
		console.log("【scanbot】getLicenseInfo error: " + e);
	}
}

//----------------------------------------
//インスタンス取得（未初期化なら初期化
//----------------------------------------

export async function getScanbot() {
	if (scanbot) {
		return scanbot;
	}
	try {
		return await initScanbot();
	} catch (e) {
		console.log("【scanbot】getScanbot error: " + e);
		return null;
	}
}

//----------------------------------------
//背面カメラのdeviceId取得
//----------------------------------------
// videoConstraints の facingMode:{ideal:"environment"} だけでは、
// 複数カメラ端末でインカメラが選ばれることがあるため、
// SDKのカメラ判定で背面のメインカメラを特定して preferredCamera に指定する。
// 取得できない場合は null を返し、facingMode 指定にフォールバックする。

export async function getBackCameraId() {
	if (backCameraId) {
		return backCameraId;
	}

	try {
		//SDKクラス未取得なら初期化してから
		if (!ScanbotSDKClass) {
			await initScanbot();
		}
		const cameras = ScanbotSDKClass?.cameras;
		if (!cameras) {
			return null;
		}

		//カメラ一覧を取得（ラベル解析：結果はSDK側でキャッシュされる
		await cameras.load("FAST");

		const camera = cameras.getMainCamera("back");
		if (camera && camera.deviceId) {
			console.log("【scanbot】back camera: " + camera.label);
			backCameraId = camera.deviceId;
			return backCameraId;
		}

		console.log("【scanbot】back camera not found → facingMode指定にフォールバック");
		return null;
	} catch (e) {
		console.log("【scanbot】getBackCameraId error: " + e);
		return null;
	}
}


//----------------------------------------
//カメラ起動（常駐コンテナへ・デコードは停止状態
//----------------------------------------
// スタート画面・スキャンボタン押下時に呼び出してカメラを起動しておく。
// スキャナーはアプリ内で1つだけ生成し、スキャン画面ごとに作り直さない。

export function startCamera() {
	return enqueue(async () => {
		//起動済みでもストリームが死んでいる場合（バックグラウンド復帰後など）は作り直す
		if (scanner) {
			if (isCameraLive()) {
				return true;
			}
			console.log("【scanbot】camera is not live → restart");
			await releaseCameraInternal();
		}

		try {
			const sdk = await getScanbot();
			if (!sdk) {
				return false;
			}

			//常駐コンテナ（body直下・非表示）を用意
			const host = createHost(SCANBOT_CONTAINER_ID);

			//コンテナが退避中（body直下）のあいだはスキャナーを生成しない。
			//※scanbot はプレビューの表示サイズを createBarcodeScanner の時点で確定させる。
			//　退避状態（160x120）で生成すると、スキャン画面のスロットへ付け替えても
			//　映像が小さいまま残る。barkoder と違って作り直しでの是正もできない
			//　（dispose → createBarcodeScanner はiOSで完了しない。D-11 / D-23）。
			//　スキャン画面の showCamera() は connectToElement のあとに startCamera() を
			//　呼ぶため、ここで見送っても画面表示時に正しいサイズで生成される
			if (!host.parentElement || host.parentElement === document.body) {
				console.log("【scanbot】コンテナが退避中のためスキャナー生成を保留");
				return false;
			}

			//背面カメラを明示指定（取得できない場合はfacingModeにフォールバック
			const cameraId = await getBackCameraId();

			console.log("【scanbot】createBarcodeScanner (camera on)");
			console.log("【scanbot】camera resolution: " + getCameraResolution());

			scanner = await withTimeout(sdk.createBarcodeScanner({
				containerId: SCANBOT_CONTAINER_ID,
				ui: { visible: false }, //独自UIを使うので非表示
				...(cameraId ? { preferredCamera: cameraId } : {}),
				mirrored: false,
				//カメラ解像度：設定画面／envで切り替える（既定はFullHD＝従来どおり
				videoConstraints: {
					facingMode: { ideal: "environment" },
					width: { ideal: getCameraResolutionSize().width },
					height: { ideal: getCameraResolutionSize().height },
					frameRate: { ideal: 30 },
				},
				scannerConfiguration: {
					//商品バーコードとQRを同時に有効化し、読み取り種別は結果側で振り分ける
					//（スキャン画面ごとに作り直すとカメラの再起動が必要になるため）
					//※フォーマットは _type ごとの設定オブジェクトで指定する（配列に含めたものが有効
					barcodeFormatConfigurations: [
						{
							_type: "BarcodeFormatUpcEanConfiguration",
							ean13: true,
							ean8: true,
							upca: true,
							upce: true,
							//雑誌コードのアドオン（EAN-2 / EAN-5）を許可
							//scandit の five_digit_add_on 相当。結果は upcEanExtension に入る
							extensions: "ALLOW_ANY",
							//同じ結果が2フレーム続かないと確定しない（既定は1）
							//1Dバーコードは誤検知しやすいため、誤読対策として2にする
							minimumNumberOfRequiredFramesWithEqualRecognitionResult: 2,
						},
						{
							_type: "BarcodeFormatCode128Configuration",
							//1Dバーコードの誤読対策（上と同じ理由
							minimumNumberOfRequiredFramesWithEqualRecognitionResult: 2,
						},
						{
							_type: "BarcodeFormatQRCodeConfiguration",
							qr: true,
							microQr: false,
							rmqr: false,
						},
					],
				},
				captureDelay: 500,
				finder: {
					visible: false,
					width: "100%",
					height: "70%",
					top: "20%",
				},
				onBarcodesDetected: onScannerResult,
				onError: (err) => {
					console.error("【scanbot】scanner error:", err);
					if (errorHandler) errorHandler(err);
				},
			}), CREATE_SCANNER_TIMEOUT, "createBarcodeScanner");

			//スキャン画面に入るまではデコードを止めておく（カメラは起動したまま
			pauseDecoding();

			//カメラの能力をログへ（AF対策の切り分け用。ストリーム確立を待つのでawaitしない
			logCameraCapabilities(hostElement(), "scanbot");

			//フォーカスの固定（設定でAUTO以外を選んでいる場合のみ。同じくawaitしない
			applyCameraFocus(hostElement(), "scanbot", getCameraFocus());

			//バックグラウンド復帰の監視を開始（初回のみ
			registerVisibilityHandlers();

			return true;
		} catch (e) {
			console.log("【scanbot】startCamera error: " + e);
			if (errorHandler) errorHandler(e);
			return false;
		}
	});
}

//----------------------------------------
//カメラ停止（スキャナー破棄
//----------------------------------------
// scandit と同じく、スキャン画面から離れたらカメラを閉じる

export function releaseCamera() {
	return enqueue(releaseCameraInternal);
}

//直列化キューの内部から呼ぶ用（enqueueしない
async function releaseCameraInternal() {
	const current = scanner;
	if (!current) return;

	scanner = null;
	decodingActive = false;

	try {
		if (typeof current.pauseDetection === "function") {
			current.pauseDetection();
		}
		if (typeof current.dispose === "function") {
			await current.dispose();
		}
	} catch (e) {
		console.log("【scanbot】releaseCamera error: " + e);
	}

	//残ったvideoを確実に停止
	const el = document.getElementById(SCANBOT_CONTAINER_ID);
	if (el) {
		el.querySelectorAll("video").forEach((video) => {
			try {
				const stream = video.srcObject;
				if (stream && typeof stream.getTracks === "function") {
					stream.getTracks().forEach((track) => {
						try {
							track.stop();
						} catch (e2) {}
					});
				}
			} catch (e2) {}
			try {
				video.pause();
			} catch (e2) {}
			try {
				video.srcObject = null;
			} catch (e2) {}
		});
		el.innerHTML = "";
	}

	console.log("【scanbot】camera released");
}

//----------------------------------------
//スキャン画面への付け替え
//----------------------------------------

export function connectToElement(slot) {
	attachHost(hostElement(), slot);
}

export function detachFromElement(slot) {
	detachHost(hostElement(), slot);
}

//----------------------------------------
//表示スタイルの当て直し
//----------------------------------------
// スキャン画面のスロットへ付け替えた直後にスタイルが確定していない場合があるため、
// 映像が出たことを確認できた時点で当て直す。
//
// ※barkoder のような「表示サイズの是正」は行わない。scanbot は退避中に
// 　スキャナーを生成しない方式にしたため、そもそもサイズがずれない（startCamera 参照）

export function refreshLayout() {
	refreshHostLayout(hostElement());
}

function hostElement() {
	return document.getElementById(SCANBOT_CONTAINER_ID);
}

//----------------------------------------
//スキャン結果ハンドラ（表示中の画面に振り分け
//----------------------------------------
// scanMode: "barcode"（商品スキャン） / "qr"（QRスキャン）

export function setScanHandler(mode, handler, onError) {
	scanMode = mode;
	scanHandler = handler;
	errorHandler = onError;
}

function onScannerResult(result) {
	if (!scanHandler) return;
	if (!result?.barcodes?.length) return;

	const barcode = result.barcodes[0];
	const isQR = String(barcode.format || "").toUpperCase() === "QR_CODE";

	//表示中の画面が扱う種別以外は無視する
	if (scanMode === "qr" && !isQR) return;
	if (scanMode === "barcode" && isQR) return;

	scanHandler(barcode);
}

//----------------------------------------
//スキャン画面の解放（デコード停止＋コンテナ退避＋カメラ停止
//----------------------------------------
// 画面遷移アニメーション中は「次の画面のマウント → 前の画面のアンマウント」の順になるため、
// 既に次の画面が使用中の場合は何もしない（次の画面のカメラを止めない）

export function releaseScreen(handler, slot) {
	//既に次の画面が使用中なら何もしない（解放しなかったことを呼び出し元へ返す
	if (scanHandler !== handler) return false;

	pauseDecoding();
	scanHandler = null;
	errorHandler = null;
	scanMode = null;

	detachHost(hostElement(), slot);

	//scandit と同じく、スキャン画面から離れたらカメラを閉じる
	releaseCamera();

	return true;
}

//----------------------------------------
//カメラのストリームが生きているか
//----------------------------------------

function isCameraLive() {
	const video = hostElement()?.querySelector("video");
	const stream = video?.srcObject;
	if (!stream || typeof stream.getVideoTracks !== "function") {
		return false;
	}
	const track = stream.getVideoTracks()[0];
	return !!track && track.readyState === "live";
}

//----------------------------------------
//バックグラウンド復帰時のカメラ再開
//----------------------------------------
// 他アプリへの切替やスリープでカメラのトラックが停止し、
// 復帰してもプレビューが黒いままになるため、可視状態に戻ったら復旧させる。

function registerVisibilityHandlers() {
	if (visibilityHandlersRegistered || typeof document === "undefined") {
		return;
	}
	visibilityHandlersRegistered = true;

	//「本来カメラが動いているべき状態か」はSDKファサードが保持しているため、
	//非表示イベントの発火有無に依存せず、表示に戻ったタイミングで判定する
	const onVisible = () => {
		if (document.hidden) return;
		if (!scanner) return;
		resumeCamera();
	};

	document.addEventListener("visibilitychange", onVisible);
	//iOS（bfcache復帰）・他アプリからの復帰対策
	window.addEventListener("pageshow", onVisible);
	window.addEventListener("focus", onVisible);
}

async function resumeCamera() {
	if (resuming) return;
	resuming = true;

	//復帰前の状態（停止処理で解除されるため先に控える
	const wantDecoding = decodingActive;

	try {
		if (isCameraLive()) {
			//ストリームが生きていれば再生し直すだけ（中断で一時停止するため
			playVideos(hostElement());
		} else {
			const ok = await refreshCameraStream();
			if (!ok) return;
		}

		//スキャン画面表示中かつ復帰前にデコード中だった場合のみ再開する
		if (scanHandler && wantDecoding) {
			resumeDecoding();
		}
	} catch (e) {
		console.log("【scanbot】resumeCamera error: " + e);
	} finally {
		resuming = false;
	}
}

//----------------------------------------
//カメラストリームの取り直し（バックグラウンド復帰用
//----------------------------------------
// スキャナーは破棄せず switchCamera でストリームだけ張り直す。
// ※dispose → createBarcodeScanner は、iOS復帰直後だとカメラの解放が
// 　間に合わず createBarcodeScanner が完了しない（ハングする）ことがあるため

async function refreshCameraStream() {
	const current = scanner;
	if (!current) return false;

	//映像が届くまでは透明にしておく（iOSの再生ボタンが一瞬見えるのを防ぐ
	setHostTransparent(hostElement(), true);

	try {
		const deviceId = backCameraId || current.getActiveCameraInfo?.()?.deviceId;

		if (deviceId && typeof current.switchCamera === "function") {
			console.log("【scanbot】visibility resume → switchCamera");
			await current.switchCamera(deviceId, false);
		} else if (typeof current.swapCameraFacing === "function") {
			console.log("【scanbot】visibility resume → swapCameraFacing");
			current.swapCameraFacing(true);
		} else {
			console.log("【scanbot】カメラ取り直しのAPIが見つからない");
			return false;
		}

		const ready = await logResumeState();
		if (ready) return true;

		//取り直しで復帰できない場合はスキャナーを作り直す（最終手段
		console.log("【scanbot】switchCamera で復帰できず → スキャナー再生成");
		await releaseCamera();
		const started = await startCamera();
		if (!started) return false;

		await logResumeState();
		return isCameraLive();
	} catch (e) {
		console.log("【scanbot】refreshCameraStream error: " + e);
		return false;
	} finally {
		setHostTransparent(hostElement(), false);
	}
}

//----------------------------------------
//復帰状態の確認（診断用
//----------------------------------------

async function logResumeState() {
	//映像が届くまで待つ（最大3秒
	let ready = false;
	for (let i = 0; i < 20; i++) {
		await new Promise((resolve) => setTimeout(resolve, 150));
		playVideos(hostElement());

		const v = hostElement()?.querySelector("video");
		if (v && v.readyState >= 3 && v.videoWidth > 0 && isCameraLive()) {
			ready = true;
			break;
		}
	}

	const video = hostElement()?.querySelector("video");
	const track = video?.srcObject?.getVideoTracks?.()[0];
	console.log(
		"【scanbot】resume state: ready=" + ready +
		" live=" + isCameraLive() +
		" readyState=" + video?.readyState +
		" videoWidth=" + video?.videoWidth +
		" paused=" + video?.paused +
		" track=" + track?.readyState
	);

	return ready;
}

//----------------------------------------
//デコードの一時停止／再開（カメラはそのまま
//----------------------------------------

export function pauseDecoding() {
	decodingActive = false;
	if (!scanner || typeof scanner.pauseDetection !== "function") return;
	try {
		scanner.pauseDetection();
	} catch (e) {
		console.log("【scanbot】pauseDetection error: " + e);
	}
}

export function resumeDecoding() {
	decodingActive = true;
	if (!scanner || typeof scanner.resumeDetection !== "function") return;
	try {
		scanner.resumeDetection();
	} catch (e) {
		console.log("【scanbot】resumeDetection error: " + e);
	}
}

//----------------------------------------
//JAN13補正：12桁のUPC-A を JAN13 に補正（先頭0を補完
//----------------------------------------

export function fixUPCEAN(code) {
	if (!code) return code;
	if (code.length === 13) return code;

	if (code.length === 12) {
		//JAN13の国コード候補
		const janCountryCodes = ["45", "49", "04", "02", "01", "03", "07"];
		for (const cc of janCountryCodes) {
			if (code.startsWith(cc[1])) {
				//例：460041903850 → 本来 0460041903850
				return "0" + code;
			}
		}
		//該当しない場合は UPC-A と判断 → 補正しない
		return code;
	}

	return code;
}
